import clsx from 'clsx'
import {
  type KeyboardEvent,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useId,
  useMemo,
  useState,
} from 'react'

interface TabsContextValue {
  value: string
  setValue(next: string): void
  baseId: string
}

const TabsContext = createContext<TabsContextValue | null>(null)

export interface TabsProps {
  defaultValue: string
  value?: string
  onValueChange?: (next: string) => void
  children: ReactNode
  className?: string
}

export function Tabs({ defaultValue, value, onValueChange, children, className }: TabsProps) {
  const [internal, setInternal] = useState(defaultValue)
  const baseId = useId()
  const current = value ?? internal

  const setValue = useCallback(
    (next: string) => {
      if (value === undefined) setInternal(next)
      onValueChange?.(next)
    },
    [value, onValueChange],
  )

  const ctx = useMemo(() => ({ value: current, setValue, baseId }), [current, setValue, baseId])

  return (
    <TabsContext.Provider value={ctx}>
      <div className={className}>{children}</div>
    </TabsContext.Provider>
  )
}

export interface TabListProps {
  children: ReactNode
  'aria-label'?: string
  className?: string
}

export function TabList({ children, className, ...rest }: TabListProps) {
  return (
    <div
      role="tablist"
      aria-label={rest['aria-label']}
      className={clsx('inline-flex items-center gap-1 border-b border-[var(--border)]', className)}
    >
      {children}
    </div>
  )
}

export interface TabProps {
  value: string
  children: ReactNode
  disabled?: boolean
}

export function Tab({ value, children, disabled }: TabProps) {
  const ctx = useContext(TabsContext)
  if (!ctx) throw new Error('<Tab> must be used inside <Tabs>')
  const selected = ctx.value === value
  const tabId = `${ctx.baseId}-tab-${value}`
  const panelId = `${ctx.baseId}-panel-${value}`

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft' && e.key !== 'Home' && e.key !== 'End')
      return
    const buttons = Array.from(
      e.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role="tab"]') ?? [],
    )
    const idx = buttons.indexOf(e.currentTarget)
    if (idx < 0) return
    let nextIdx = idx
    if (e.key === 'ArrowRight') nextIdx = (idx + 1) % buttons.length
    if (e.key === 'ArrowLeft') nextIdx = (idx - 1 + buttons.length) % buttons.length
    if (e.key === 'Home') nextIdx = 0
    if (e.key === 'End') nextIdx = buttons.length - 1
    e.preventDefault()
    buttons[nextIdx]?.focus()
    buttons[nextIdx]?.click()
  }

  return (
    <button
      type="button"
      role="tab"
      id={tabId}
      aria-selected={selected}
      aria-controls={panelId}
      tabIndex={selected ? 0 : -1}
      disabled={disabled}
      onClick={() => !disabled && ctx.setValue(value)}
      onKeyDown={onKeyDown}
      className={clsx(
        '-mb-px h-9 px-3 text-[13px] border-b-2 outline-none transition-colors',
        'focus-visible:shadow-[var(--focus-ring)] rounded-t-[var(--radius-sm)]',
        selected
          ? 'border-[var(--accent)] text-[var(--text)]'
          : 'border-transparent text-[var(--text-2)] hover:text-[var(--text)]',
        disabled && 'opacity-50 cursor-not-allowed',
      )}
    >
      {children}
    </button>
  )
}

export interface TabPanelProps {
  value: string
  children: ReactNode
  className?: string
}

export function TabPanel({ value, children, className }: TabPanelProps) {
  const ctx = useContext(TabsContext)
  if (!ctx) throw new Error('<TabPanel> must be used inside <Tabs>')
  const tabId = `${ctx.baseId}-tab-${value}`
  const panelId = `${ctx.baseId}-panel-${value}`
  const hidden = ctx.value !== value
  return (
    <div
      role="tabpanel"
      id={panelId}
      aria-labelledby={tabId}
      hidden={hidden}
      tabIndex={0}
      className={clsx('pt-3 outline-none focus-visible:shadow-[var(--focus-ring)]', className)}
    >
      {!hidden ? children : null}
    </div>
  )
}
